import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import { Participant } from 'livekit-client';
import { ParticipantReaction, Reaction, ReactionType } from 'features/Reactions/types';
import { REACTION_DISPLAY_DURATION } from 'features/Reactions/consts';

class ReactionsStore {
    @observable public reactions: Reaction[] = [];
    @observable public participantReactions: Map<string, ParticipantReaction> = new Map();

    private participantReactionTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();

    public constructor() {
        makeAutoObservable(this);
    }

    @action
    public cleanup = () => {
        this.participantReactionTimeouts.forEach((timeout) => clearTimeout(timeout));
        this.participantReactionTimeouts.clear();
        this.reactions = [];
        this.participantReactions.clear();
    };

    @action
    public addReaction = (reaction: Reaction) => {
        // игнорируем реакции когда вкладка скрыта
        if (document.hidden) return;
        this.reactions = [...this.reactions, reaction];
    };

    @action
    public removeReaction = (reactionId: string) => {
        this.reactions = this.reactions.filter((r) => r.id !== reactionId);
    };

    @action
    public markReactionAsFading = (reactionId: string) => {
        this.reactions = this.reactions.map((reaction) =>
            reaction.id === reactionId ? { ...reaction, isFading: true } : reaction,
        );
    };

    @action
    public setParticipantReaction = (participantId: string, type: ReactionType, timestamp: number) => {
        // Очищаем предыдущий таймер, если есть
        const existingTimeout = this.participantReactionTimeouts.get(participantId);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        const newMap = new Map(this.participantReactions);
        newMap.set(participantId, { type, timestamp });
        this.participantReactions = newMap;

        // Устанавливаем таймер на удаление из плитки через 5 секунд
        const timeout = setTimeout(() => {
            runInAction(() => {
                this.removeParticipantReaction(participantId);
            });
            this.participantReactionTimeouts.delete(participantId);
        }, REACTION_DISPLAY_DURATION);
        this.participantReactionTimeouts.set(participantId, timeout);
    };

    @action
    public removeParticipantReaction = (participantId: string) => {
        const newMap = new Map(this.participantReactions);
        newMap.delete(participantId);
        this.participantReactions = newMap;
    };

    public getParticipantReaction = (participant: Participant): ParticipantReaction | null => {
        return this.participantReactions.get(participant.identity) || null;
    };
}

export const reactionsStore = new ReactionsStore();
